import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../LanguageContext";
import { Mail, Smartphone, BarChart3, Share, Globe, Bug, Lightbulb, Star } from "lucide-react";
import "./Support.css";
import "../App.css";

export default function SupportPage() {
  const { t, language, toggleLanguage } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="support-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <Link to="/" className="nav-logo-link">
              <img
                src="/belly-flow-logo.svg"
                alt="Belly Flow"
                className="nav-logo-img"
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="nav-links desktop-nav">
            <Link to="/" className="nav-link">
              {t('features')}
            </Link>
            <Link to="/" className="nav-link">
              {t('about')}
            </Link>
            <Link to="/privacy" className="nav-link">
              {t('privacy')}
            </Link>
            <Link to="/support" className="nav-link active">
              {t('support')}
            </Link>
            
            {/* Language Switcher */}
            <div className="language-switcher">
              <button 
                className="language-button"
                onClick={toggleLanguage}
                aria-label={t('language')}
              >
                {language === 'sv' ? 'EN' : 'SV'}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Right Side */}
          <div className="mobile-nav-right">
            <div className="mobile-language-switcher-top">
              <button 
                className="language-button mobile-top"
                onClick={toggleLanguage}
              >
                {language === 'sv' ? 'EN' : 'SV'}
              </button>
            </div>

            <button 
              className="mobile-menu-button"
              onClick={toggleMobileMenu}
              aria-label="Öppna meny"
            >
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-links">
            <Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('features')}
            </Link>
            <Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('about')}
            </Link>
            <Link to="/privacy" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('privacy')}
            </Link>
            <Link to="/support" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('support')}
            </Link>
          </div>
        </div>
      </nav>

      <div className="support-container">
        <h1 className="support-title">{t('supportTitle')}</h1>

        <div className="support-header">
          <p className="support-subtitle">
            {t('supportSubtitle')}
          </p>
        </div>

        <div className="support-content">
          {/* Kontaktinformation */}
          <section className="support-section">
            <h2 className="support-section-title">{t('contactUs')}</h2>
            <div className="contact-card primary">
              <div className="contact-icon"><Mail size={28} strokeWidth={1.5} /></div>
              <h3>{t('emailSupport')}</h3>
              <p className="contact-email">
                <a href="mailto:support@bellyflow.se">support@bellyflow.se</a>
              </p>
            </div>
          </section>

          {/* Vanliga frågor */}
          <section className="support-section">
            <h2 className="support-section-title">{t('frequentlyAskedQuestions')}</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3 className="faq-question">{t('howToCreateAccount')}</h3>
                <p className="faq-answer">
                  {t('accountCreationAnswer')}
                </p>
              </div>

              <div className="faq-item">
                <h3 className="faq-question">{t('howToLogSymptoms')}</h3>
                <p className="faq-answer">
                  {t('symptomLoggingAnswer')}
                </p>
              </div>

              <div className="faq-item">
                <h3 className="faq-question">{t('isMyDataSafe')}</h3>
                <p className="faq-answer">
                  {t('dataSafetyAnswer')}
                </p>
              </div>

              <div className="faq-item">
                <h3 className="faq-question">
                  {t('canIExportData')}
                </h3>
                <p className="faq-answer">
                  {t('dataExportAnswer')}
                </p>
              </div>

              <div className="faq-item">
                <h3 className="faq-question">
                  {t('howDoesResearchGrantWork')}
                </h3>
                <p className="faq-answer">
                  {t('researchGrantAnswer')}
                </p>
              </div>
            </div>
          </section>

          {/* App-funktioner */}
          <section className="support-section">
            <h2 className="support-section-title">{t('appFeatures')}</h2>
            <div className="features-grid">
              <div className="feature-item">
                <div className="feature-icon"><Smartphone size={24} strokeWidth={1.5} /></div>
                <h3>{t('easyLogging')}</h3>
                <p>{t('easyLoggingDescription')}</p>
              </div>

              <div className="feature-item">
                <div className="feature-icon"><BarChart3 size={24} strokeWidth={1.5} /></div>
                <h3>{t('analysisAndTrends')}</h3>
                <p>{t('analysisAndTrendsDescription')}</p>
              </div>

              <div className="feature-item">
                <div className="feature-icon"><Share size={24} strokeWidth={1.5} /></div>
                <h3>{t('export')}</h3>
                <p>{t('exportDescription')}</p>
              </div>

              <div className="feature-item">
                <div className="feature-icon"><Globe size={24} strokeWidth={1.5} /></div>
                <h3>{t('languages')}</h3>
                <p>{t('languagesDescription')}</p>
              </div>
            </div>
          </section>

          {/* Felsökning */}
          <section className="support-section">
            <h2 className="support-section-title">{t('troubleshooting')}</h2>
            <div className="troubleshooting">
              <div className="trouble-item">
                <h3>{t('appDoesNotStart')}</h3>
                <ul>
                  <li>{t('checkLatestAppVersion')}</li>
                  <li>{t('restartPhone')}</li>
                  <li>{t('checkInternetConnection')}</li>
                </ul>
              </div>

              <div className="trouble-item">
                <h3>{t('cannotLogIn')}</h3>
                <ul>
                  <li>{t('checkEmailAddress')}</li>
                  <li>{t('useForgotPassword')}</li>
                  <li>{t('checkIfEmailBlocked')}</li>
                </ul>
              </div>

              <div className="trouble-item">
                <h3>{t('dataNotVisible')}</h3>
                <ul>
                  <li>{t('checkCorrectAccount')}</li>
                  <li>{t('syncAppByPullingDown')}</li>
                  <li>{t('checkInternetConnection')}</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Feedback */}
          <section className="support-section">
            <h2 className="support-section-title">{t('giveFeedback')}</h2>
            <div className="feedback-section">
              <p>
                {t('appreciateFeedback')}
                {t('sendSuggestions')}
                <a href="mailto:support@bellyflow.se" className="support-link">
                  {t('supportEmail')}
                </a>
              </p>

              <div className="feedback-types">
                <div className="feedback-type">
                  <h4><Bug size={16} strokeWidth={1.5} /> {t('bugReport')}</h4>
                  <p>{t('reportProblems')}</p>
                </div>

                <div className="feedback-type">
                  <h4><Lightbulb size={16} strokeWidth={1.5} /> {t('suggestions')}</h4>
                  <p>{t('shareIdeas')}</p>
                </div>

                <div className="feedback-type">
                  <h4><Star size={16} strokeWidth={1.5} /> {t('review')}</h4>
                  <p>{t('leaveReview')}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Ladda ner */}
          <section className="support-section">
            <h2 className="support-section-title">{t('downloadBellyFlow')}</h2>
            <div className="download-section">
              <p className="download-text">
                {t('readyToStartJourney')}
              </p>
              <div className="store-badges">
                <a 
                  href="https://apps.apple.com/se/app/belly-flow/id6751012592"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-badge"
                >
                  <img src="/app-store-badge.svg" alt="Download on the App Store" />
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.bellyflow.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-badge"
                >
                  <img src="/google-play-badge.svg" alt="Get it on Google Play" />
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
